import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { EnablementApi } from '@portal/pkg-api/shared'
import { data } from '@portal/pkg-api/helpers'
import type { Enablements, IlmPermission } from './enablements'
import type { Scope, ScopeOptions } from './scope'
import { ScopedEnablements } from './scopedEnablements'
import { useScope } from './scopeContext'

export const ScopedEnablementQueries = {
  scopedEnablements: () => ({
    queryKey: ['shared.enablement.scopedEnablements'],
    queryFn: async () => new ScopedEnablements(await data(EnablementApi.getScopedEnablements())),
  }),
}

export function useScopedEnablements(): UseQueryResult<ScopedEnablements> {
  return useQuery<ScopedEnablements>({
    ...ScopedEnablementQueries.scopedEnablements(),
    staleTime: 0,
  })
}

function useResolvedScope(opts?: ScopeOptions): Scope | undefined {
  const ambient = useScope()
  return opts?.global ? undefined : (opts ?? ambient)
}

/**
 * The enablements that apply in the ambient scope, the scope given as `opts`, or across every
 * scope with `{ global: true }`. `undefined` while loading.
 */
export function useEnablements(opts?: ScopeOptions): Enablements | undefined {
  const scope = useResolvedScope(opts)
  const { data: scopedEnablements } = useScopedEnablements()
  return scopedEnablements?.for(scope)
}

/**
 * Whether the user has an enablement in the ambient scope, the scope given as `opts`, or across
 * every scope with `{ global: true }`. `null` while it cannot be determined yet.
 */
export function useHasEnablement(
  target: string,
  permission: IlmPermission,
  opts?: ScopeOptions,
): boolean | null
export function useHasEnablement(
  target: string,
  permission: IlmPermission,
  qualifier: string,
  opts?: ScopeOptions,
): boolean | null
export function useHasEnablement(
  target: string,
  permission: IlmPermission,
  qualifierOrOpts?: string | ScopeOptions,
  opts?: ScopeOptions,
): boolean | null {
  const [qualifier, scopeOptions] =
    typeof qualifierOrOpts === 'string' ? [qualifierOrOpts, opts] : [undefined, qualifierOrOpts]
  const enablements = useEnablements(scopeOptions)

  if (!enablements) {
    return null
  }

  return qualifier
    ? enablements.hasQualifiedEnablement(target, permission, qualifier)
    : enablements.hasStandardEnablement(target, permission)
}
